/** Client-side CSV export shared by the engineering worklist exports (blocked
 *  coordination pairs, unrated devices) and the GIS-queue worklists. Pure
 *  string assembly lives in `toCsv`; `exportCsv` wraps it in a Blob download.
 *  No backend round trip — the data is already on the client.
 *
 *  SHARED CONTRACT (do not change the signature or the field rules — cluster
 *  s3-c10 consumes both): exportCsv(filename: string, rows: Record<string,
 *  unknown>[]): void, with formula-safe fields (see csvField). */

/** Leading =, +, -, or @ makes Excel and Google Sheets treat a TEXT cell as a
 *  formula and evaluate it when the file is opened. Optional leading whitespace
 *  does not save you — the spreadsheet trims it first. */
const FORMULA_TRIGGER = /^\s*[=+\-@]/

/** One CSV field.
 *
 *  1. null/undefined -> ''.
 *  2. Numbers render verbatim — this MUST come before the formula check, or a
 *     legitimate negative value (-42, a -3.2 % delta) would be exported as the
 *     text '-42 and stop being a number in the spreadsheet.
 *  3. FORMULA NEUTRALIZATION: a string (or a JSON-stringified object) that
 *     starts with a trigger character gets a single-quote prefix — the
 *     spreadsheet convention for "this is literal text". Every column exported
 *     in this stage carries GIS- or user-authored free text (device names,
 *     notes, platform/gis values, a change request's proposed_value, joined
 *     candidate names), and the stage smoke opens these files in Excel.
 *  4. RFC-4180 escaping LAST, over the possibly-prefixed value: quote when it
 *     contains a comma, quote, or newline; double any embedded quotes. A
 *     prefixed value usually needs no quoting — the apostrophe is not a
 *     special character in RFC 4180. */
function csvField(value: unknown): string {
  if (value === null || value === undefined) return ''
  if (typeof value === 'number' || typeof value === 'bigint') return String(value)
  const raw = typeof value === 'object' ? JSON.stringify(value) : String(value)
  const s = FORMULA_TRIGGER.test(raw) ? `'${raw}` : raw
  return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
}

/** Assemble a CSV string. The header is the union of row keys in first-seen
 *  order, so rows with differing shapes still export every column. */
export function toCsv(rows: Record<string, unknown>[]): string {
  if (rows.length === 0) return ''
  const header: string[] = []
  for (const row of rows) {
    for (const key of Object.keys(row)) {
      if (!header.includes(key)) header.push(key)
    }
  }
  const lines = [header.map(csvField).join(',')]
  for (const row of rows) {
    lines.push(header.map(k => csvField(row[k])).join(','))
  }
  return lines.join('\r\n') + '\r\n'
}

/** Download `rows` as `filename` via a transient anchor. A UTF-8 BOM is
 *  prepended so Excel opens the file with the right encoding. */
export function exportCsv(filename: string, rows: Record<string, unknown>[]): void {
  const blob = new Blob(['\uFEFF' + toCsv(rows)], { type: 'text/csv;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}
